import { useEffect, useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import type { AuthTokens } from "@orbit/api-zod";
import { useAuth } from "../hooks/useAuth";
import { customerFetch } from "../lib/customerFetch";
import { canonicalizePhone } from "../lib/phone";

/**
 * Phase 14 — Claim a marketplace code.
 *
 * Three rendered states, all on `/claim/:code`:
 *
 *   1. Loading — initial preview fetch is pending.
 *   2. Preview + auth — signed-in customer hits "Claim" → POST
 *      `/customer/claim/:code` → on success show "+N points / +M
 *      stamps" and link to /cards.
 *   3. Preview + signup — anonymous customer fills name/phone/consent
 *      → POST `/customer/claim/:code/with-signup` → atomic create
 *      account + claim code + sign in via `useAuth`.
 *
 * The CLAIMED / EXPIRED / VOIDED branches all bail out before showing
 * either action, with a friendly message.
 */

interface PreviewResponse {
  code: string;
  platform: "SHOPEE" | "LAZADA" | "TIKTOK" | "OTHER";
  tenantName: string;
  grossAmountCentavos: number;
  currency: string;
  status: "UNCLAIMED" | "CLAIMED" | "EXPIRED" | "VOIDED";
  expiresAt: string;
  alreadyClaimedByCurrentCustomer: boolean;
}

interface ClaimSuccess {
  code: string;
  status: "CLAIMED";
  pointsCredited: number;
  stampsCredited: number;
  tenantName: string;
}

interface SignupClaimResponse {
  customerId: string;
  tokens: AuthTokens;
  claim: ClaimSuccess;
}

export function ClaimCodePage() {
  const [, params] = useRoute<{ code: string }>("/claim/:code");
  const code = (params?.code ?? "").toUpperCase();
  const { isAuthenticated, signIn } = useAuth();
  const [, navigate] = useLocation();

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const r = await customerFetch<PreviewResponse>(
        `/api/v1/customer/claim/${encodeURIComponent(code)}/preview`,
      );
      if (cancelled) return;
      if (r.ok) setPreview(r.data);
      else setLoadErr(r.message);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return <main className="mx-auto max-w-md p-6">Loading code…</main>;
  }
  if (loadErr || !preview) {
    return (
      <main className="mx-auto max-w-md p-6">
        <h1 className="text-xl font-bold text-navy">Code not found</h1>
        <p className="mt-2 text-sm text-ink-dark">
          {loadErr ?? "We couldn't find that claim code."}
        </p>
        <a href="/claim" className="mt-4 inline-block text-navy underline">
          Try entering it again
        </a>
      </main>
    );
  }

  const expired = new Date(preview.expiresAt) < new Date();
  const blocking =
    preview.status === "EXPIRED" ||
    expired ||
    preview.status === "VOIDED" ||
    (preview.status === "CLAIMED" && !preview.alreadyClaimedByCurrentCustomer);

  return (
    <main className="mx-auto max-w-md p-6">
      <header>
        <p className="text-xs uppercase text-stone-500">{preview.platform}</p>
        <h1 className="mt-1 text-2xl font-bold text-navy">
          {preview.tenantName}
        </h1>
        <p className="mt-1 font-mono text-sm text-ink-dark">{preview.code}</p>
        <p className="mt-2 text-sm text-ink-dark">
          Order total:{" "}
          <strong>
            {(preview.grossAmountCentavos / 100).toFixed(2)} {preview.currency}
          </strong>
        </p>
      </header>

      {preview.status === "CLAIMED" && preview.alreadyClaimedByCurrentCustomer && (
        <div className="mt-6 rounded bg-green-50 p-4 text-sm text-green-800">
          You already claimed this code. The reward is on your card.
          <div className="mt-3">
            <a href="/cards" className="font-semibold text-navy underline">
              View your cards →
            </a>
          </div>
        </div>
      )}

      {blocking && (
        <div className="mt-6 rounded bg-amber-50 p-4 text-sm text-amber-900">
          {preview.status === "VOIDED"
            ? "This code was voided by the merchant. Please reach out to them if you think this was a mistake."
            : preview.status === "CLAIMED"
              ? "This code has already been claimed by another account."
              : "This code has expired. Please contact the merchant for a replacement."}
        </div>
      )}

      {!blocking &&
        preview.status === "UNCLAIMED" &&
        (isAuthenticated ? (
          <ClaimAction code={code} navigate={navigate} />
        ) : (
          <SignupClaim code={code} signIn={signIn} navigate={navigate} />
        ))}
    </main>
  );
}

/** Authenticated single-button claim. */
function ClaimAction({
  code,
  navigate,
}: {
  code: string;
  navigate: (to: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<ClaimSuccess | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    const r = await customerFetch<ClaimSuccess>(
      `/api/v1/customer/claim/${encodeURIComponent(code)}`,
      { method: "POST", body: JSON.stringify({}) },
    );
    setBusy(false);
    if (r.ok) setOk(r.data);
    else setErr(r.message);
  }

  if (ok) {
    return (
      <div className="mt-6 rounded bg-green-50 p-4 text-sm text-green-800">
        <p className="font-semibold">
          Claimed at {ok.tenantName}!
        </p>
        <p className="mt-1">
          {ok.pointsCredited > 0 && <>+{ok.pointsCredited.toFixed(2)} points </>}
          {ok.stampsCredited > 0 && <>+{ok.stampsCredited} stamps </>}
          added to your card.
        </p>
        <button
          type="button"
          onClick={() => navigate("/cards")}
          className="mt-3 rounded bg-navy px-4 py-2 font-semibold text-white"
        >
          View my cards
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={go}
        disabled={busy}
        className="mt-6 w-full rounded bg-navy px-5 py-3 font-semibold text-white disabled:bg-stone-300"
      >
        {busy ? "Claiming…" : "Claim this code"}
      </button>
      {err && (
        <p className="mt-3 text-sm text-red-600">{err}</p>
      )}
    </>
  );
}

/** Anonymous signup-and-claim form. */
function SignupClaim({
  code,
  signIn,
  navigate,
}: {
  code: string;
  signIn: (id: string, t: AuthTokens) => void;
  navigate: (to: string) => void;
}) {
  const [name, setName] = useState("");
  const [phoneRaw, setPhoneRaw] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [partial, setPartial] = useState<string | null>(null);

  const canonical = useMemo(() => canonicalizePhone(phoneRaw), [phoneRaw]);
  const canSubmit =
    name.trim().length > 0 &&
    canonical !== null &&
    password.length >= 8 &&
    consent &&
    !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !canonical) return;
    setBusy(true);
    setErr(null);
    setPartial(null);
    const r = await customerFetch<SignupClaimResponse>(
      `/api/v1/customer/claim/${encodeURIComponent(code)}/with-signup`,
      {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          phone: canonical,
          password,
        }),
      },
    );
    setBusy(false);
    if (r.ok) {
      signIn(r.data.customerId, r.data.tokens);
      navigate("/cards");
      return;
    }
    // Phase 14 partial-success: account was created but the claim
    // itself lost the race (very rare). The server still hands back
    // tokens in `details` so the user isn't stranded.
    if (r.status === 409 && r.code.startsWith("CLAIM_")) {
      const det = r.details as
        | { customerId?: string; tokens?: AuthTokens }
        | undefined;
      if (det?.customerId && det.tokens) {
        signIn(det.customerId, det.tokens);
      }
      setPartial(r.message);
      return;
    }
    setErr(r.message);
  }

  if (partial) {
    return (
      <div className="mt-6 rounded bg-amber-50 p-4 text-sm text-amber-900">
        <p>{partial}</p>
        <button
          type="button"
          onClick={() => navigate("/cards")}
          className="mt-3 rounded bg-navy px-4 py-2 font-semibold text-white"
        >
          Go to my cards
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
      <p className="text-sm text-ink-dark">
        Create your free Orbit card to claim this reward — takes 30 seconds.
      </p>
      <label htmlFor="name" className="label">
        Your name
      </label>
      <input
        id="name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="rounded border border-stone-300 px-3 py-2"
        autoComplete="name"
      />
      <label htmlFor="phone" className="label">
        Mobile number
      </label>
      <input
        id="phone"
        type="tel"
        value={phoneRaw}
        onChange={(e) => setPhoneRaw(e.target.value)}
        placeholder="09171234567"
        className="rounded border border-stone-300 px-3 py-2"
        autoComplete="tel"
      />
      <label htmlFor="password" className="label">
        Password
        <span className="ml-1 text-xs font-normal text-stone-500">
          (8+ characters)
        </span>
      </label>
      <input
        id="password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        minLength={8}
        maxLength={72}
        className="rounded border border-stone-300 px-3 py-2"
        autoComplete="new-password"
      />
      <label className="flex items-start gap-2 text-xs text-ink-dark">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5"
        />
        I agree Orbit can store my name + phone for loyalty rewards.
      </label>
      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-2 rounded bg-navy px-5 py-3 font-semibold text-white disabled:bg-stone-300"
      >
        {busy ? "Creating account…" : "Sign up & claim"}
      </button>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <p className="mt-1 text-center text-xs text-stone-500">
        Already have an account?{" "}
        <a
          href={`/login?next=${encodeURIComponent(`/claim/${code}`)}`}
          className="text-navy underline"
        >
          Sign in
        </a>{" "}
        first, then come back.
      </p>
    </form>
  );
}
